"use client";

import React, { JSX, useMemo } from "react";

import DOMPurify from "dompurify";

import { novaFonts } from "@/styles/nova-fonts.mjs";
import { generateScopeClass } from "@/utils/general";

const validElements = ["h1", "h2", "h3", "h4", "h5", "h6", "p", "span", "button", "li"] as const;
const invalidElements = validElements.filter((el) => !["span", "button"].includes(el));
const showWarning = typeof window === "object" && window.location.hostname.includes("localhost");

type ValidElement = (typeof validElements)[number];

type ResponsiveClasses = Record<string, string> & {
  default?: string;
};

export type TextElementNovaPropsBase = {
  element?: ValidElement;
  className?: string;
  styles?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onKeyPress?: React.KeyboardEventHandler<HTMLElement>;
  classes?: ResponsiveClasses;
  sanitize?: boolean;
};

export type TextElementNovaPropsWithText = TextElementNovaPropsBase & {
  text: string | number | React.ReactElement;
  children?: never;
};

type TextElementNovaPropsWithChildren = TextElementNovaPropsBase & {
  text?: never;
  children: React.ReactNode;
};

/*
 * Explanation:
 *   TextElementNovaPropsWithText: requires text and forbids children.
 *
 *   TextElementNovaPropsWithChildren: requires children and forbids text.
 *
 *   The union (|) means you must provide exactly one of these shapes.
 */

export type TextElementNovaProps = TextElementNovaPropsWithText | TextElementNovaPropsWithChildren;

function generateResponsiveCSS(classes: ResponsiveClasses, scopeClass: string): string {
  let css = "";

  const breakpoints = Object.keys(classes)
    .sort((a, b) => Number(a) - Number(b))
    .filter((bp) => bp !== "default");

  const buildStyle = (obj: Record<string, string>) =>
    Object.entries(obj)
      .map(([k, v]) => `\t${k}: ${v} !important;`)
      .join("");

  breakpoints.forEach((bp) => {
    const font = novaFonts[classes[bp] as keyof typeof novaFonts];
    if (font) {
      const style = buildStyle(font[Number(bp) < 1024 ? 1 : 2]);
      css += `@media (min-width: ${bp}px) { .${scopeClass} { ${style} } }\n`;
    } else if (showWarning) {
      //console.warn(`Invalid class passed: ${classes[bp]}`);
    }
  });

  return css;
}

/*
 * interface ElementProps {
 *   className?: string;
 * }
 */

export const TextElementNova: React.FC<TextElementNovaProps> = ({
  text,
  element = "p",
  onClick,
  onKeyPress,
  classes = {},
  className = "",
  styles,
  sanitize = false,
  children,
}) => {
  const hasDefault = !!classes.default;

  const scopeClass = useMemo(() => {
    return hasDefault ? generateScopeClass(JSON.stringify(classes)) : "";
  }, [classes, hasDefault]);

  const css = useMemo(() => {
    return hasDefault ? generateResponsiveCSS(classes, scopeClass) : "";
  }, [classes, scopeClass, hasDefault]);

  /*
   * Prevent invalid nested tags inside <p>. Browsers auto-close <p> around block
   * elements, which causes React hydration mismatches with dangerouslySetInnerHTML.
   */
  const processedText = useMemo(() => {
    if (typeof text === "string" && element === "p") {
      let result = text;
      const nestedBlockElements = [...invalidElements, "div"];
      nestedBlockElements.forEach((el) => {
        if (result.includes(`<${el}`)) {
          if (showWarning) {
            //console.warn(`TextElement: <${el.toUpperCase()}> in <P> replaced with <SPAN>`, text);
          }
          const reg1 = new RegExp(`(<${el})`, "igm");
          const reg2 = new RegExp(`(</${el})`, "igm");
          result = result.replace(reg1, "<span").replace(reg2, "</span");
        }
      });
      return result;
    }
    return text;
  }, [text, element]);

  const Element: keyof JSX.IntrinsicElements = validElements.includes(element) ? element : "p";

  const fullClassName = useMemo(() => {
    let combined = `textelement nova  ${className} ${classes?.default || ""}`;
    if (hasDefault) combined += ` ${scopeClass} `;
    if (!["text-nova-", "text-master-"].some((prefix) => combined.includes(prefix))) {
      combined += " text-nova-base-black";
    }
    return combined.trim();
  }, [className, classes, scopeClass, hasDefault]);

  const isReactElement = React.isValidElement(text);
  // const safeHTML = !children && !isReactElement ? String(processedText) : "";

  return (
    <>
      {css && <style dangerouslySetInnerHTML={{ __html: css }} />}
      {sanitize && typeof processedText === "string" && !children && !isReactElement ? (
        <Element
          className={fullClassName}
          style={styles}
          onClick={onClick}
          onKeyPress={onKeyPress}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedText) }}
        />
      ) : children || isReactElement ? (
        <Element className={fullClassName} style={styles} onClick={onClick} onKeyPress={onKeyPress}>
          {children ?? text}
        </Element>
      ) : (
        <Element
          className={fullClassName}
          style={styles}
          onClick={onClick}
          onKeyPress={onKeyPress}
          dangerouslySetInnerHTML={{ __html: String(processedText) }}
        />
      )}
    </>
  );
};
